import React, { useEffect, useState, useCallback, useRef } from "react";
import { CustomModal } from "@/components/Modal";
import { PageTitle } from "@/components/Title";
import {
  Box,
  Stack,
  Typography,
  Button,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { SavedPlotFeedGrid, hasSharedRow } from "./SavedPlotFeedGrid";
import { SavedPlotFilterModal } from "./SavedPlotFilterModal";
import { SavedPlotFilterChips } from "./SavedPlotFilterChips";
import { DeletePlotRequestConfirmModal } from "./DeletePlotRequestConfirmModal";
import {
  type SavedPlotRow,
  type SavedMapPlotRequestDetail,
  type SavedPlotFilterValues,
  type PlotRequestTag,
  type MapPlotRequest,
} from "../../types";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import { useMapPlotRequest } from "@/features/map/hooks/useMapPlotRequest";
import { useFilteredRows } from "@/features/map/hooks/useFilteredRows";
import { extractUniqueTags } from "@/features/map/utils/tagUtils";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { isAxiosError } from "axios";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { useFeatureFlags } from "@/configs/featureFlag";
import { exportPlotRequestDetails } from "@/utils/mapPlotRequestExport";
import { useAdditionalFieldsConfig } from "@/features/map/hooks/useAdditionalFieldsConfig";
import { useApiClient } from "@/hooks/useApiClient";
import { getPlotRequestDetailAPI } from "@/features/map/api/getPlotRequestDetailAPI";
import { formatNumberWithComma } from "@/utils/formatNumber";

type OwnerFilter = "all" | "mine" | "shared";

const filterRowsByOwner = (
  rows: SavedPlotRow[],
  ownerFilter: OwnerFilter
): SavedPlotRow[] => {
  if (ownerFilter === "mine") {
    return rows.filter((row) => row.isEditable !== false);
  }
  if (ownerFilter === "shared") {
    return rows.filter((row) => row.isEditable === false);
  }
  return rows;
};

/**
 * 作成者の絞り込みを切り替えたあとも見えている行の選択だけを残す。
 * 見えなくなった行の選択を残すと、画面に無いリストを削除・地図反映してしまう。
 */
export const keepVisibleSelection = (
  rows: SavedPlotRow[],
  ownerFilter: OwnerFilter,
  selectedIds: GridSelectionModel
): GridSelectionModel => {
  const visibleIds = new Set(
    filterRowsByOwner(rows, ownerFilter).map((row) => row.id)
  );
  return selectedIds.filter((id) => visibleIds.has(Number(id)));
};

const INITIAL_FILTER_VALUES: SavedPlotFilterValues = {
  name: null,
  countFrom: null,
  countTo: null,
  createdAtFrom: null,
  createdAtTo: null,
  updatedAtFrom: null,
  updatedAtTo: null,
  tags: [],
};

interface LoadedDatasetInfo {
  id: number;
  name: string;
}

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onPlotSelected?: (
    plotDetails: SavedMapPlotRequestDetail[],
    datasetInfo?: LoadedDatasetInfo[]
  ) => void;
  /** いま地図に出しているリストのID。選んでも足さない */
  loadedDatasetIds?: number[];
  /** いま地図に出しているピンの数。上限はこれと選んだリストの件数を合わせて見る */
  loadedPlotCount?: number;
}

/**
 * 一度に地図へ出せるピンの上限（地図に出しているピンと、選んだリストの件数の合計）。
 * ピンは Canvas に描くので件数による重さは小さく、上限は取得する応答の大きさ
 * （1万件で約5MB）で決めている。BE の読み出しに上限は無い。
 * 取得の前に判定するので、選んだリストは土地以外も含む物件リストの件数で数える
 * （地図に出るピンはこれ以下になる）
 */
export const MAX_PLOT_COUNT = 20_000;

export const SavedPlotModal: React.FC<IProps> = (props) => {
  const {
    open,
    setOpen,
    onPlotSelected,
    loadedDatasetIds = [],
    loadedPlotCount = 0,
  } = props;
  const { apiClient } = useApiClient();
  // カスタム（追加）項目の設定情報を取得
  const { availableFields } = useAdditionalFieldsConfig();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const { refetch, fetchPlotDetails, isError } = useMapPlotRequest(open);
  const [allRows, setAllRows] = useState<SavedPlotRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);
  // データがすでに取得されているかを追跡するref
  const dataFetchedRef = useRef(false);
  const { crm: crmFeatureFlag } = useFeatureFlags();
  const crm = Boolean(crmFeatureFlag);

  // フィルター関連のstate
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<SavedPlotFilterValues>(
    INITIAL_FILTER_VALUES
  );

  // 削除確認モーダルの状態
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  // MapPlotRequestから取得したタグ一覧
  const [availableTags, setAvailableTags] = useState<PlotRequestTag[]>([]);

  // フィルター適用後のデータ (crmフラグがONの時のみフィルタリング)
  const filteredRows = useFilteredRows(allRows, filters);
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>("all");
  const showOwnerFilter = hasSharedRow(allRows);
  const rows = filterRowsByOwner(
    crm ? filteredRows : allRows,
    showOwnerFilter ? ownerFilter : "all"
  );
  // 他のユーザーから共有されたリストは地図に出せるが、削除は作成者だけができる
  const deletableSelectedIds = selectedIds.filter((id) =>
    allRows.some((row) => row.id === id && row.isEditable !== false)
  ) as number[];
  const hasUndeletableSelection =
    deletableSelectedIds.length < selectedIds.length;

  const handleRowSelectionChange = (
    newSelectionModel: GridSelectionModel
  ): void => {
    setSelectedIds(newSelectionModel);
  };

  // 強制的に再取得するためのフラグ
  const [forceRefresh, setForceRefresh] = useState(false);

  /**
   * APIから取得したMapPlotRequestデータをSavedPlotRow形式に整形する
   */
  const formatPlotData = useCallback(
    (data: MapPlotRequest[]): SavedPlotRow[] => {
      return data.map((request) => ({
        id: request.id,
        selected: false,
        name: request.name,
        count: request.count,
        userName: request.userName,
        isEditable: request.isEditable,
        createdAt: request.createdAt,
        updatedAt: request.updatedAt,
        tags: request.tags,
      }));
    },
    []
  );

  /**
   * データ取得とステート更新を行う
   */
  const fetchAndUpdateData = useCallback(async (): Promise<void> => {
    setIsModalLoading(true);
    try {
      const result = await refetch();
      if (result && result.length > 0) {
        const formattedRows = formatPlotData(result);
        setAllRows(formattedRows);

        // crmフラグがONの時のみタグ一覧を抽出（tag名で重複を除く）
        if (crm) {
          setAvailableTags(extractUniqueTags(result));
        }

        dataFetchedRef.current = true;
      } else {
        setAllRows([]);
        setAvailableTags([]);
      }
    } catch (error) {
      console.error("データ取得エラー:", error);
    } finally {
      setIsModalLoading(false);
    }
  }, [refetch, formatPlotData, crm]);

  /**
   * 更新ボタン押下時のハンドラー
   * useEffectとの依存関係を切り離すため独立させている
   */
  const handleRefreshData = (): void => {
    dataFetchedRef.current = false;
    void fetchAndUpdateData();
  };

  // フィルター適用ハンドラ
  const handleApplyFilters = (newFilters: SavedPlotFilterValues): void => {
    setFilters(newFilters);
  };

  // フィルタークリアハンドラ
  const handleClearFilters = (): void => {
    setFilters(INITIAL_FILTER_VALUES);
  };

  // 削除成功後のハンドラー
  const handleDeleteSuccess = (): void => {
    // 選択をクリア
    setSelectedIds([]);
    // データを再取得
    dataFetchedRef.current = false;
    void fetchAndUpdateData();
  };

  /**
   * モーダルが開いたときのデータ取得制御
   * - CRMフラグが有効な場合は毎回データを再取得
   * - それ以外は重複取得を防止
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    // CRMフラグが有効な場合、モーダルが開くたびにデータを再取得
    if (crm) {
      dataFetchedRef.current = false;
    }

    // すでにデータが取得されている場合は何もしない
    if (dataFetchedRef.current && !forceRefresh) {
      return;
    }

    void fetchAndUpdateData();
    setForceRefresh(false);
  }, [open, crm, forceRefresh, fetchAndUpdateData]);

  /**
   * 物件リストの特定行のデータを CSV/Excel 形式でダウンロードする
   *
   * @param row - 選択された物件リスト行データ
   * @param formatType - ダウンロードするファイル形式 ('csv' | 'excel')
   */
  const handleDownloadRow = async (
    row: SavedPlotRow,
    formatType: "csv" | "excel"
  ): Promise<void> => {
    try {
      // 選択した行（物件リスト）の全物件詳細データ（失敗含む）をAPIからフェッチ
      const response = await getPlotRequestDetailAPI(apiClient, row.id);
      if (!response?.details) {
        toast.error("データの取得に失敗しました。");
        return;
      }
      // 共通エクスポートユーティリティを呼び出し、ダウンロードを実行
      exportPlotRequestDetails(
        formatType,
        row.name,
        response.details,
        availableFields
      );
    } catch (error) {
      console.error("ダウンロード用データの取得に失敗しました:", error);
      toast.error("データの取得に失敗しました。");
    }
  };

  const handlePlotToMap = async (): Promise<void> => {
    if (selectedIds.length === 0) {
      toast.error("プロットするデータを選択してください。");
      return;
    }

    // すでに地図に出しているリストは取り直さない
    const selectedRows = rows.filter(
      (row) =>
        selectedIds.includes(row.id) && !loadedDatasetIds.includes(row.id)
    );
    if (selectedRows.length === 0) {
      toast.info("選んだ物件リストはすでに地図に反映されています。");
      setSelectedIds([]);
      setOpen(false);
      return;
    }
    const totalCount = selectedRows.reduce((sum, row) => sum + row.count, 0);

    if (totalCount === 0) {
      toast.error("プロット可能なデータが存在しません。");
      return;
    }

    if (loadedPlotCount + totalCount > MAX_PLOT_COUNT) {
      toast.error(
        `地図に反映できるのは合計${formatNumberWithComma(
          MAX_PLOT_COUNT
        )}件までです。選んだ物件リストの件数（土地以外も含む）: ${formatNumberWithComma(
          totalCount
        )}件${
          loadedPlotCount > 0
            ? `（地図に反映済みの${formatNumberWithComma(
                loadedPlotCount
              )}件と合わせて${formatNumberWithComma(
                loadedPlotCount + totalCount
              )}件）`
            : ""
        }`
      );
      return;
    }

    setIsModalLoading(true);
    try {
      const selectedMapPlotRequestDetails = await fetchPlotDetails(
        selectedRows.map((row) => row.id)
      );

      // 土地のみをフィルタリング（経緯:https://trustart-group.slack.com/archives/C04HA6M7SMB/p1763444573320059?thread_ts=1763442229.347609&cid=C04HA6M7SMB）
      const landOnly = selectedMapPlotRequestDetails.filter(
        (detail) => detail.realEstateType === "土地"
      );
      if (landOnly.length === 0) {
        toast.error(
          "地図に反映できるデータがありません。地図に反映されるのは土地のみです"
        );
        setIsModalLoading(false);
        return;
      }

      if (onPlotSelected) {
        // 選択されたMapPlotRequestの識別名情報を作成
        const selectedDatasetInfo: LoadedDatasetInfo[] = selectedRows.map(
          (row) => ({
            id: row.id,
            name: row.name,
          })
        );
        onPlotSelected(landOnly, selectedDatasetInfo);
      }

      setSelectedIds([]);
      setOpen(false);
    } catch (error) {
      console.error("プロット詳細の取得に失敗しました:", error);

      if (isAxiosError(error) && error.response?.status === 404) {
        toast.error("プロット可能なデータがありませんでした。");
      } else {
        toast.error("プロット詳細の取得に失敗しました。");
      }
    } finally {
      setIsModalLoading(false);
    }
  };

  const hasData = rows.length > 0;
  const showLoading = isModalLoading;
  const canPlotToMap = selectedIds.length > 0 && hasData;

  return (
    <CustomModal
      isOpen={open}
      handleClose={(
        event: Record<string, unknown>,
        reason: "backdropClick" | "escapeKeyDown"
      ): void => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        setOpen(false);
      }}
      ariaLabelledby="saved-plot-info-modal"
      ariaDescribedby="saved-plot-info-modal-description"
      width={"80%"}
      height={"80%"}
      closeOnBackdropClick={false}
      fullScreenOnMobile
    >
      {/* SP は全画面なので、一覧を残りの高さいっぱいに広げる（PC は従来どおり 80%） */}
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* SP では見出しと「閉じる」を1行目に、フィルター・削除を折り返して2行目に出す */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 1, [MOBILE_BREAKPOINT]: 2 },
              minWidth: 0,
            }}
          >
            <PageTitle>
              {crm
                ? "保存した物件リスト一覧"
                : "保存されたプロット情報を呼び出す"}
            </PageTitle>
            {crm && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterListIcon />}
                  onClick={() => {
                    setFilterModalOpen(true);
                  }}
                >
                  フィルター
                </Button>
                <Tooltip
                  title={
                    hasUndeletableSelection
                      ? "他のユーザーが作成したリストは削除できません（自分のリストだけを削除します）"
                      : ""
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setDeleteModalOpen(true);
                      }}
                      disabled={deletableSelectedIds.length === 0}
                    >
                      削除
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
            {showOwnerFilter && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={ownerFilter}
                onChange={(_, value: OwnerFilter | null) => {
                  if (value !== null) {
                    setOwnerFilter(value);
                    setSelectedIds((prev) =>
                      keepVisibleSelection(
                        crm ? filteredRows : allRows,
                        value,
                        prev
                      )
                    );
                  }
                }}
                aria-label="作成者で絞り込む"
              >
                <ToggleButton value="all">すべて</ToggleButton>
                <ToggleButton value="mine">自分のリスト</ToggleButton>
                <ToggleButton value="shared">共有されたリスト</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            {!crm && (
              <Tooltip title="データを再取得する">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshData}
                  disabled={showLoading}
                >
                  更新
                </Button>
              </Tooltip>
            )}
            {crm && (
              <Button
                variant="outlined"
                onClick={() => {
                  setOpen(false);
                }}
              >
                × 閉じる
              </Button>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            mt: 2,
            height: { xs: "auto", [MOBILE_BREAKPOINT]: "80%" },
            flex: { xs: 1, [MOBILE_BREAKPOINT]: "none" },
            minHeight: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {crm && (
            <SavedPlotFilterChips
              filters={filters}
              onFilterChange={setFilters}
              onClearAll={handleClearFilters}
            />
          )}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {showLoading ? (
              <Stack alignItems="center">
                <CircularProgress sx={{ my: "20px" }} />
              </Stack>
            ) : isError ? (
              <Stack alignItems="center">
                <Typography color="error" sx={{ my: "20px" }}>
                  データの取得に失敗しました。
                </Typography>
              </Stack>
            ) : (
              <SavedPlotFeedGrid
                handleRowSelectionChange={handleRowSelectionChange}
                rows={rows}
                selectedIds={selectedIds}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                crm={crm}
                availableTags={availableTags}
                onDownloadRow={handleDownloadRow}
              />
            )}
          </Box>
          <Box
            sx={{
              mt: 2,
              mb: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              ※ プロット可能なデータは、地図に反映済みのものと合わせて最大で
              {formatNumberWithComma(MAX_PLOT_COUNT)}件までです
            </Typography>
            {crm && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                ※
                地図に反映されるのは土地のみです（建物・区分建物は除外されます）
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              ※ {crm && "csvでの"}
              データの登録はバックグラウンドで処理されます。処理完了後にメールでお知らせが届きましたら、データを取得できます
            </Typography>
          </Box>
          <Stack
            direction="row"
            justifyContent="end"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 2 }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setOpen(false);
              }}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                void handlePlotToMap();
              }}
              disabled={!canPlotToMap}
            >
              {crm ? "地図に反映する" : "地図にプロットする"}
            </Button>
          </Stack>
        </Box>
      </Box>

      {crm && (
        <>
          <SavedPlotFilterModal
            open={filterModalOpen}
            onClose={() => {
              setFilterModalOpen(false);
            }}
            availableTags={availableTags}
            initialFilters={filters}
            onApply={handleApplyFilters}
          />
          <DeletePlotRequestConfirmModal
            open={deleteModalOpen}
            setOpen={setDeleteModalOpen}
            selectedIds={deletableSelectedIds}
            onSuccess={handleDeleteSuccess}
          />
        </>
      )}
    </CustomModal>
  );
};
